/*
 * File: abbyCadabbyApi.ts
 * Project: rosita
 * -----
 * Last Modified: 03 Mar 2022 12:22:31 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { ApiMultipleResponse } from 'models/ApiModels';
import { Application, ApplicationParams, EditApplicationPayload } from 'models/Application';
import { Business, CreateBusinessPayload, EditBusinessPayload, GetBusinessesParams } from 'models/Business';
import { CreateDivisionPayload, Division, EditDivisionPayload } from 'models/Division';
import { ReduxState } from 'redux/store';

const baseUrl = process.env.REACT_APP_ABBY_CADABBY_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_ABBY_CADABBY;

export const abbyCadabbyApi = createApi({
  reducerPath: 'abbyCadabbyApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState, endpoint }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
      }

      if (endpoint !== 'updateApplicationLogo') headers.set('Content-Type', 'application/json');

      return headers;
    }
  }),
  tagTypes: ['Division', 'Application', 'Business'],
  endpoints: (builder) => ({
    getApplication: builder.query<Application, string>({
      query: (applicationId) => ({
        url: `/applications/${applicationId}`
      }),
      providesTags: ['Application']
    }),
    getApplications: builder.query<ApiMultipleResponse<Application>, ApplicationParams>({
      query: (params) => ({
        url: `/applications/`,
        params
      }),
      providesTags: ['Application']
    }),
    updateApplicationLogo: builder.mutation<Application, { applicationId: string; payload: FormData }>({
      query: ({ applicationId, payload }) => ({
        url: `/applications/${applicationId}/logo`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Business', 'Division', 'Application']
    }),
    updateApplication: builder.mutation<Business, { applicationId: string; payload: EditApplicationPayload }>({
      query: ({ applicationId, payload }) => ({
        url: `/applications/${applicationId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['Business', 'Division']
    }),
    deleteApplication: builder.mutation<Application, { applicationId: string }>({
      query: ({ applicationId }) => ({
        url: `/applications/${applicationId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Application']
    }),
    recoverApplication: builder.mutation<Application, { applicationId: string }>({
      query: ({ applicationId }) => ({
        url: `/applications/${applicationId}/recover`
      }),
      invalidatesTags: ['Application']
    }),
    getBusiness: builder.query<Business, string>({
      query: (businessId) => ({
        url: `/businesses/${businessId}`
      }),
      providesTags: ['Business']
    }),
    updateBusiness: builder.mutation<Business, { businessId: string; payload: EditBusinessPayload }>({
      query: ({ businessId, payload }) => ({
        url: `/businesses/${businessId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['Business', 'Division']
    }),
    updateBusinessLogo: builder.mutation<Business, { businessId: string; payload: FormData }>({
      query: ({ businessId, payload }) => ({
        url: `/businesses/${businessId}/logo`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Business', 'Division']
    }),
    createBusiness: builder.mutation<Business, { payload: CreateBusinessPayload }>({
      query: ({ payload }) => ({
        url: '/businesses',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Business']
    }),
    getDivision: builder.query<Division, string>({
      query: (divisionId) => ({
        url: `/divisions/${divisionId}`
      }),
      providesTags: ['Division']
    }),
    getDivisions: builder.query<Division, void>({
      query: () => ({
        url: `/divisions/`
      }),
      providesTags: ['Division']
    }),
    updateDivision: builder.mutation<Division, { divisionId: string; payload: EditDivisionPayload }>({
      query: ({ divisionId, payload }) => ({
        url: `/divisions/${divisionId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['Division', 'Business']
    }),
    createDivision: builder.mutation<Division, { payload: CreateDivisionPayload }>({
      query: ({ payload }) => ({
        url: `/divisions`,
        method: 'POST',
        body: payload
      })
    }),
    getBusinesses: builder.query<ApiMultipleResponse<Business>, void>({
      query: () => ({
        url: '/businesses?includeDivisions=true'
      }),
      providesTags: ['Business']
    }),
    getBusinessNameConatins: builder.query<ApiMultipleResponse<Business>, GetBusinessesParams>({
      query: (params) => ({
        url: '/businesses/',
        params
      }),
      providesTags: ['Business']
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useGetBusinessesQuery,
  useGetBusinessQuery,
  useGetVersionQuery,
  useGetApiNameQuery,
  useGetDivisionQuery,
  useUpdateBusinessMutation,
  useUpdateDivisionMutation,
  useGetBusinessNameConatinsQuery,
  useCreateBusinessMutation,
  useCreateDivisionMutation,
  useGetDivisionsQuery,
  useUpdateBusinessLogoMutation,
  useGetApplicationQuery,
  useUpdateApplicationLogoMutation,
  useGetApplicationsQuery,
  useDeleteApplicationMutation,
  useUpdateApplicationMutation,
  useRecoverApplicationMutation,
  useLazyGetBusinessNameConatinsQuery
} = abbyCadabbyApi;
